// src/features/shared-scope/components/SharedLinksActivityLog.tsx
// ────────────────────────────────────────────────────────────────────────────
// Shared Links Activity Log — Master / Detail Layout
//
// Left panel:  DarkDataTable with time-ago, status, scope, tenant, hits
// Right panel: SharedLinkDetailPanel with full parametrization + recreate
// Top bar:     Summary counters (active / expired / revoked) + search + filter
//
// Zero-prop top-level component. Data owned by useSharedLinksActivity hook.
// PAC Glass design system — dark glassmorphism, transparent blur surfaces.
// ────────────────────────────────────────────────────────────────────────────
import React from 'react';
import DarkDataTable from '@/shared/components/ui/DarkDataTable';
import SharedLinkDetailPanel from './SharedLinkDetailPanel';
import { useSharedLinksActivity } from '../hooks/useSharedLinksActivity';
import { timeAgo } from '@/utils/date';
import type { ActivityLogEntry, SharedLinkStatus } from '../api/sharedScopeApi';
import './shroomy-neon.css';

// ── Status badge ─────────────────────────────────────────────────────────

function statusBadgeClass(status: string): string {
  if (status === 'active')  return 'text-[#39FF14]';
  if (status === 'revoked') return 'text-[#FF3366]';
  if (status === 'expired') return 'text-[#FFD700]';
  return 'text-[#D098EF]';
}

const SCOPE_TYPE_ICONS: Record<string, string> = {
  'table-ops':   '🎮',
  'iot-devices': '📡',
  'generic':     '📦',
};

// ── Counter Card ─────────────────────────────────────────────────────────

function CounterCard({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className="rounded-md border border-[#1A0A2E] bg-[#08000F] px-3 py-1.5 flex items-center gap-2">
      <span className={`text-sm font-semibold ${color}`}>{count}</span>
      <span className="text-[10px] text-[#6B4D8A] uppercase tracking-wider">{label}</span>
    </div>
  );
}

// ── Filter Pills ─────────────────────────────────────────────────────────

const FILTER_OPTIONS: Array<{ value: SharedLinkStatus | 'all'; label: string }> = [
  { value: 'all',     label: 'All' },
  { value: 'active',  label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
];

// ── Component ────────────────────────────────────────────────────────────

export default function SharedLinksActivityLog() {
  const {
    items,
    counters,
    selectedEntry,
    isLoading,
    error,
    selectedToken,
    setSelectedToken,
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,
    handleRevoke,
    handleRecreate,
    isRevoking,
    isRecreating,
    recreatedUrl,
  } = useSharedLinksActivity();

  return (
    <div className="shroomy-neon h-full min-h-0 rounded-md border border-[#1A0A2E] bg-[#030005] flex flex-col">
      {/* ── Summary Header ──────────────────────────────────────────── */}
      <div className="px-3 py-2 border-b border-[#1A0A2E] flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-3">
          <div className="text-sm text-[#D098EF] font-medium">URL Activity Log</div>
          <CounterCard label="Active"  count={counters.active}  color="text-[#39FF14]" />
          <CounterCard label="Expired" count={counters.expired} color="text-[#FFD700]" />
          <CounterCard label="Revoked" count={counters.revoked} color="text-[#FF3366]" />
        </div>

        <div className="flex items-center gap-3">
          {/* Status filter */}
          <div className="flex items-center gap-1">
            {FILTER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                  statusFilter === opt.value
                    ? 'border-[#400095] text-[#D098EF] bg-[#400095]/30'
                    : 'border-transparent text-[#6B4D8A] hover:text-[#D098EF]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <input
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search links…"
            className="w-[16rem] h-7 px-2 rounded-md bg-[#08000F] border border-[#1A0A2E] text-[#C4A0E0] placeholder:text-[#3A1F5E] text-xs outline-none focus:ring-1 focus:ring-[#400095]"
          />

          <div className="text-[10px] text-[#6B4D8A] whitespace-nowrap">
            {items.length} link{items.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* ── Master / Detail ──────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 flex">
        {/* Left: Activity table */}
        <div className="min-h-0 min-w-0 flex flex-col border-r border-[#1A0A2E]" style={{ flex: 6 }}>
          <div className="p-3 flex-1 min-h-0 overflow-auto">
            {isLoading && <div className="text-sm text-[#8A6AAF]">Loading activity…</div>}
            {error && (
              <div className="text-sm text-[#D098B8]">
                Failed to load: {(error as Error)?.message ?? String(error)}
              </div>
            )}

            {!isLoading && !error && items.length === 0 && (
              <div className="text-sm text-[#6B4D8A]">
                {searchText || statusFilter !== 'all' ? 'No matches.' : 'No shared links created yet.'}
              </div>
            )}

            {!isLoading && !error && items.length > 0 && (
              <DarkDataTable<ActivityLogEntry>
                columns={[
                  {
                    header: 'Time',
                    thClassName: 'whitespace-nowrap',
                    tdClassName: 'text-[#B8A0E0] whitespace-nowrap',
                    render: e => (
                      <div title={new Date(e.created_at).toLocaleString()}>
                        {timeAgo(e.created_at)}
                      </div>
                    ),
                  },
                  {
                    header: 'Status',
                    thClassName: 'whitespace-nowrap',
                    tdClassName: 'whitespace-nowrap',
                    render: e => (
                      <span className={`text-[11px] px-1.5 py-0.5 font-medium ${statusBadgeClass(e.status)}`}>
                        {e.status.toUpperCase()}
                      </span>
                    ),
                  },
                  {
                    header: 'Label',
                    tdClassName: 'max-w-[16rem]',
                    render: e => (
                      <div className="flex items-center gap-1.5 truncate" title={e.label || '—'}>
                        <span>{SCOPE_TYPE_ICONS[e.scope_type] ?? '🔗'}</span>
                        <span className="truncate font-semibold text-[#FF8C00]">{e.label || '—'}</span>
                      </div>
                    ),
                  },
                  {
                    header: 'Scope',
                    tdClassName: 'text-[#00BFFF] max-w-[14rem]',
                    render: e => {
                      const s = e.scope_keys
                        ? `${e.scope_ns} · ${e.scope_keys.length} keys`
                        : `${e.scope_ns}:${e.scope_key}`;
                      return <div className="truncate font-mono text-[10px]" title={s}>{s}</div>;
                    },
                  },
                  {
                    header: 'Tenant',
                    tdClassName: 'text-[#FFD700] whitespace-nowrap',
                    render: e => <span>{e.tenant_id}</span>,
                  },
                  {
                    header: 'Hits',
                    tdClassName: 'text-[#B8A0E0] whitespace-nowrap text-right',
                    render: e => (
                      <span title={`${e.unique_visitors} unique`}>
                        {e.access_count}
                      </span>
                    ),
                  },
                ]}
                rows={items}
                getRowKey={e => e.token}
                onRowClick={e => setSelectedToken(e.token)}
                isRowSelected={e => e.token === selectedToken}
                emptyMessage="No shared links."
              />
            )}
          </div>
        </div>

        {/* Right: Detail panel */}
        <div className="min-h-0" style={{ flex: 4 }}>
          <SharedLinkDetailPanel
            entry={selectedEntry}
            onRevoke={handleRevoke}
            onRecreate={handleRecreate}
            isRevoking={isRevoking}
            isRecreating={isRecreating}
            recreatedUrl={recreatedUrl}
          />
        </div>
      </div>
    </div>
  );
}
